import { FlatTreeControl } from '@angular/cdk/tree';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { isNullOrUndefined } from '@sla-ng/core';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { distinctUntilChanged, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostUnitGroupOverview } from '../../../models/cost-unit-group-overview.model';
import { ICostUnitGroup } from '../../../models/cost-unit-group.model';
import { ICostUnitGroupLayout } from '../../../models/layout/cost-unit-group-layout.model';
import { ICostUnitGroupState } from '../../../services/facades/master-data/cost-unit-group-state.model';
import { CostUnitGroupFacade } from '../../../services/facades/master-data/cost-unit-group.facade';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { CostUnitGroupLayoutService } from '../../../services/layout/cost-unit-group-layout.service';
import {
    CostUnitGroupContributionMarginLevelAssignmentsDialogComponent,
} from './cost-unit-group-contribution-margin-level-assignments-dialog/cost-unit-group-contribution-margin-level-assignments.dialog.component';
import { CostUnitGroupCostUnitAssignmentsDialogComponent } from './cost-unit-group-cost-unit-assignments-dialog/cost-unit-group-cost-unit-assignments.dialog.component';
import { CostUnitGroupDialogComponent } from './cost-unit-group-dialog/cost-unit-group-dialog.component';

@Component({
    templateUrl: './cost-unit-group.page.html',
    styleUrls: [ './cost-unit-group.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostUnitGroupPage implements OnInit, OnDestroy {



    public treeControl: FlatTreeControl<ICostUnitGroupOverview> = new FlatTreeControl(node => node.level, node => node.expandable);
    public treeFlattener: MatTreeFlattener<ICostUnitGroupOverview, ICostUnitGroupOverview>;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public dataSource: MatTreeFlatDataSource<ICostUnitGroupOverview, ICostUnitGroupOverview>;

    public layout: ICostUnitGroupLayout;

    public costUnitGroupState$: Observable<ICostUnitGroupState>;
    public sharedDataState$: Observable<ISharedDataState>;
    private destroy$: Subject<void> = new Subject();

    constructor(private myCostUnitGroupFacade: CostUnitGroupFacade,
                private myMatDialog: MatDialog,
                private myCostUnitGroupLayoutService: CostUnitGroupLayoutService,
                private mySharedDataFacade: SharedDataFacade) {
    }

    public transformer = (node: ICostUnitGroupOverview, level: number): ICostUnitGroupOverview => {
        const transformed = node;
        transformed.expandable = !!node.children && node.children.length > 0;
        transformed.level = level;
        return transformed;
    };

    public ngOnInit(): void {
        this.treeFlattener = new MatTreeFlattener(
            this.transformer, node => node.level,
            node => node.expandable, node => node.children);
        this.dataSource = new MatTreeFlatDataSource<ICostUnitGroupOverview, ICostUnitGroupOverview>(this.treeControl, this.treeFlattener);

        this.layout = this.myCostUnitGroupLayoutService.getCostUnitGroupLayout();
        this.costUnitGroupState$ = this.myCostUnitGroupFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();

        this.sharedDataState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ISharedDataState) => s.costUnitGroups),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: ICostUnitGroupOverview[]) => {
                this.dataSource.data = data;
            });
        void firstValueFrom(this.mySharedDataFacade.fetchCostUnitGroups());
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myCostUnitGroupFacade.clearState();
    }

    public onOpenDialog(costUnitGroup?: ICostUnitGroup): void {
        const dialog: MatDialogRef<CostUnitGroupDialogComponent> = this.myMatDialog.open(CostUnitGroupDialogComponent, {
            width: '600px',
            data: isNullOrUndefined(costUnitGroup) ? {} : costUnitGroup,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void firstValueFrom(this.mySharedDataFacade.fetchCostUnitGroups(true));
            }
        });
    }
    public async onOpenAssignedCostUnitsDialog(costUnitGroup: ICostUnitGroupOverview): Promise<void> {
        await this.myCostUnitGroupFacade.setSelectedCostUnitGroup(costUnitGroup);
        const dialog: MatDialogRef<CostUnitGroupCostUnitAssignmentsDialogComponent> = this.myMatDialog.open(CostUnitGroupCostUnitAssignmentsDialogComponent, {
            data: isNullOrUndefined(costUnitGroup) ? {} : costUnitGroup,
        });
        dialog.afterClosed().subscribe(() => {
            void firstValueFrom(this.mySharedDataFacade.fetchCostUnitGroups(true));
        });
    }

    public onDeleteCostUnitGroup(costUnitGroup: ICostUnitGroup): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.cost-unit-group.confirm-delete.label',
                messageParams: { name: costUnitGroup.name }
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostUnitGroupFacade.deleteCostUnitGroup(costUnitGroup.id);
                void firstValueFrom(this.mySharedDataFacade.fetchCostUnitGroups(true));
            }
        });
    }

    public async onOpenAssignedContributionMarginLevelsDialog(costUnitGroup: ICostUnitGroup): Promise<void> {
        await this.myCostUnitGroupFacade.setSelectedCostUnitGroup(costUnitGroup);
        const dialog: MatDialogRef<CostUnitGroupContributionMarginLevelAssignmentsDialogComponent> = this.myMatDialog.open(
            CostUnitGroupContributionMarginLevelAssignmentsDialogComponent, {});
        dialog.afterClosed().subscribe(() => {
            void firstValueFrom(this.mySharedDataFacade.fetchCostUnitGroups(true));
        });
    }
}
